/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Html Dom Node 를 확장한다.
 *
 * @file /src/utils/DomList.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 6. 19.
 */
import { Dom } from '@utils/Dom';
import { EventMap } from '@utils/Events';
export class DomList<E extends HTMLElement = HTMLElement> {
    doms: Dom<E>[];

    /**
     * Dom 목록객체를 생성한다.
     *
     * @param elements - HTML 엘리먼트
     */
    constructor(elements: NodeList | HTMLElement[]) {
        this.doms = [];
        elements.forEach((element: any) => {
            if (element instanceof HTMLElement) {
                this.doms.push(Dom.init<E>(element));
            }
        });
    }

    /**
     * 목록에서 특정위치의 Dom 객체를 가져온다.
     *
     * @param index - 가져올 Dom 의 위치
     * @returns Dom
     */
    get(index: number): Dom<E> | null {
        return this.doms[index] ?? null;
    }

    /**
     * 전체 목록을 가져온다.
     *
     * @returns 전체 목록
     */
    getList(): Dom<E>[] {
        return this.doms;
    }

    /**
     * 전체 목록의 갯수를 가져온다.
     *
     * @returns count
     */
    getCount(): number {
        return this.doms.length;
    }

    /**
     * 목록에서 forEach() 함수를 실행한다.
     *
     * @param callback - forEach 함수
     */
    forEach(callback: (dom: Dom<E>, index?: number, domList?: Dom<E>[]) => void | boolean): void {
        this.doms.forEach(callback);
    }

    /**
     * 목록에서 some() 함수를 실행한다.
     *
     * @param callback - some 함수
     * @return callback 함수 중 하나라도 참인 값을 반환하면 ture, 그렇지 않다면 false
     */
    some(callback: (dom: Dom<E>, index?: number, domList?: Dom<E>[]) => boolean): boolean {
        return this.doms.some(callback);
    }

    /**
     * 목록에서 every() 함수를 실행한다.
     *
     * @param callback - every 함수
     * @return callback 함수 모두 참인 값을 반환하면 ture, 그렇지 않다면 false
     */
    every(callback: (dom: Dom<E>, index?: number, domList?: Dom<E>[]) => boolean): boolean {
        return this.doms.every(callback);
    }

    /**
     * 목록의 모든 HTML 엘리먼트의 Attribute 값을 설정한다.
     *
     * @param key - 설정할 Attribute키
     * @param value - 설정할 값
     * @returns DomList
     */
    setAttr(key: string, value: string): this {
        this.doms.forEach((dom) => {
            dom.setAttr(key, value);
        });

        return this;
    }

    /**
     * 목록의 모든 HTML 엘리먼트의 Attribute 값을 제거한다.
     *
     * @param key - 설정할 Attribute키
     * @returns DomList
     */
    removeAttr(key: string): this {
        this.doms.forEach((dom) => {
            dom.removeAttr(key);
        });

        return this;
    }

    /**
     * 목록의 모든 HTML 엘리먼트의 Attribute 값을 가져온다.
     *
     * @param key - 가져올 Attribute키
     * @returns 값
     */
    getAttrs(key: string): string[] {
        let attrs: string[] = [];
        this.doms.forEach((dom) => {
            attrs.push(dom.getAttr(key));
        });

        return attrs;
    }

    /**
     * 목록의 모든 HTML 엘리먼트의 Data-Attribute 또는 Data 값을 설정한다.
     *
     * @param key - 값을 가져올 Data-Attribute 키
     * @param value - 값
     * @param is_dom - HTML 엘리먼트에 data-attribute 를 생성할 지 여부
     * @returns DomList
     */
    setData(key: string, value: any, is_dom: boolean = true): this {
        this.doms.forEach((dom) => {
            dom.setData(key, value, is_dom);
        });

        return this;
    }

    /**
     * 목록의 모든 HTML 엘리먼트의 Data-Attribute 또는 Data 값을 가져온다.
     *
     * @param key - 값을 가져올 Data 키
     * @returns 값
     */
    getDataset(key: string): any[] {
        let dataset: string[] = [];
        this.doms.forEach((dom) => {
            dataset.push(dom.getData(key));
        });

        return dataset;
    }

    /**
     * 목록의 모든 HTML 엘리먼트 스타일을 지정한다.
     *
     * @param key - 스타일명
     * @param value - 스타일값
     * @returns DomList
     */
    setStyle(key: string, value: string): this {
        this.doms.forEach((dom) => {
            dom.setStyle(key, value);
        });
        return this;
    }

    /**
     * 목록의 모든 HTML 엘리먼트에 스타일시트(class)를 추가한다.
     *
     * @param className - 추가할 클래스명
     * @returns DomList
     */
    addClass(...className: string[]): this {
        this.doms.forEach((dom) => {
            dom.addClass(...className);
        });
        return this;
    }

    /**
     * 목록의 모든 HTML 엘리먼트의 스타일시트(class)를 제거한다.
     *
     * @param className - 제거할 클래스명
     * @returns DomList
     */
    removeClass(...className: string[]): this {
        this.doms.forEach((dom) => {
            dom.removeClass(...className);
        });
        return this;
    }

    /**
     * 목록의 모든 HTML 엘리먼트의 스타일시트(class)가 있는 경우 제거하고, 없는 경우 추가한다.
     *
     * @param className - 토글할 클래스명
     * @param force - 강제 적용여부
     * @returns DomList
     */
    toggleClass(className: string, force: boolean = false): this {
        this.doms.forEach((dom) => {
            dom.toggleClass(className, force);
        });
        return this;
    }

    /**
     * 목록의 모든 HTML 엘리먼트에 텍스트를 추가한다.
     *
     * @param text - 추가할 텍스트명
     * @returns DomList
     */
    text(text: string): this {
        this.doms.forEach((dom) => {
            dom.text(text);
        });
        return this;
    }

    /**
     * 목록의 모든 HTML 엘리먼트의 제일 처음에 Dom 을 추가한다.
     *
     * @param child - 추가할 Dom 객체
     * @returns DomList
     */
    prepend(child: Dom): this {
        this.doms.forEach((dom) => {
            dom.prepend(child);
        });
        return this;
    }

    /**
     * 목록의 모든 HTML 엘리먼트의 제일 마지막에 Dom 을 추가한다.
     *
     * @param child - 추가할 Dom 객체
     * @returns DomList
     */
    append(child: Dom): this {
        this.doms.forEach((dom) => {
            dom.append(child);
        });
        return this;
    }

    /**
     * 목록의 모든 HTML 엘리먼트를 보인다.
     */
    show(): void {
        this.doms.forEach((dom) => {
            dom.show();
        });
    }

    /**
     * 목록의 모든 HTML 엘리먼트를 숨긴다.
     */
    hide(): void {
        this.doms.forEach((dom) => {
            dom.hide();
        });
    }

    /**
     * 목록의 모든 HTML 엘리먼트의 비활성 여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     */
    setDisabled(disabled: boolean): void {
        this.doms.forEach((dom) => {
            dom.setDisabled(disabled);
        });
    }

    /**
     * 목록의 모든 HTML 엘리먼트를 활성화한다.
     */
    enable(): void {
        this.doms.forEach((dom) => {
            dom.enable();
        });
    }

    /**
     * 목록의 모든 HTML 엘리먼트를 비활성화한다.
     *
     * @param is_loading - 로딩 인디케이터를 보일지 여부
     */
    disable(is_loading: boolean = false): void {
        this.doms.forEach((dom) => {
            dom.disable(is_loading);
        });
    }

    /**
     * 목록의 모든 HTML 엘리먼트를 제거한다.
     */
    remove(): void {
        this.doms.forEach((dom) => {
            dom.remove();
        });
    }

    /**
     * 목록의 모든 HTML 엘리먼트에 이벤트를 추가한다.
     *
     * @param name - 추가할 이벤트명
     * @param listener - 이벤트리스너
     * @returns DomList
     */
    on<K extends keyof EventMap & string>(
        name: K,
        listener: (me: Dom, e: EventMap[K]) => any,
        options?: boolean | AddEventListenerOptions
    ): this {
        this.doms.forEach((dom) => {
            dom.on(
                name,
                (me: Dom, e: EventMap[K]) => {
                    return listener(me, e);
                },
                options
            );
        });
        return this;
    }

    /**
     * HTML 엘리먼트에 이벤트를 제거한다.
     *
     * @param name - 제거할 이벤트명
     * @param listener - 이벤트리스너
     * @returns DomList
     */
    off<K extends keyof EventMap & string>(name: K, listener: (me: Dom<E>, e: EventMap[K]) => any): this {
        this.doms.forEach((dom) => {
            dom.off(name, listener);
        });
        return this;
    }

    /**
     * 이벤트를 발생시킨다.
     *
     * @param name - 발생시킬 이벤트명
     * @param e - 원본 이벤트객체
     */
    trigger(name: string, e: Event = null): void {
        this.doms.forEach((dom) => {
            dom.trigger(name, e);
        });
    }
}
